import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Avatar,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { getStories, updateStory } from '../../../redux/apis/retailer/stories';
import { trackRetailerClick } from '../../../redux/apis/distrubutor/promoApis';
import { buildOrderUrlWithSource } from '../../../utils/cartSourceUtils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Story {
  isActive: boolean;
  id: number;
  mediaUrl: string;
  mediaType: 'image' | 'video';
  caption: string;
  updatedAt: string;
  createdAt: string;
  items?: number[] | null;
}

interface StoriesProps {
  open: boolean;
  onClose: () => void;
}

const IMAGE_DURATION = 5000; // ms
const DEFAULT_VIDEO_DURATION = 15000; // fallback if metadata not yet loaded

const Stories: React.FC<StoriesProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const logo = useSelector((state: RootState) => state.auth.logo);
  const navigate = useNavigate();

  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [videoDurationMs, setVideoDurationMs] = useState<number | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const viewedIdsRef = useRef<Set<number>>(new Set());

  const currentStory = stories[currentStoryIndex];

  // ---- Fetch on open ----
  useEffect(() => {
    if (open) {
      // Reset the viewed set for a fresh session so re-opens still record analytics
      viewedIdsRef.current = new Set();
      fetchStories();
    }
  }, [open]);

  const fetchStories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getStories();
      const fetchedStories: any = (response as any)?.data || response || [];
      setStories(fetchedStories.stories || []);
    } catch (err: any) {
      console.error('Error fetching stories:', err);
      setError(err.response?.data?.message || t('stories.failedToFetchStories'));
    } finally {
      setLoading(false);
    }
  };

  // ---- View tracking (deduped) ----
  const trackStoryView = useCallback(async (storyId: number) => {
    if (viewedIdsRef.current.has(storyId)) return;
    viewedIdsRef.current.add(storyId);
    try {
      await updateStory(storyId.toString());
    } catch (err) {
      console.error('Error tracking story view:', err);
      // allow a retry on next visit if it failed
      viewedIdsRef.current.delete(storyId);
    }
  }, []);

  // Fire exactly once per story id (per modal session)
  useEffect(() => {
    if (!open || !currentStory) return;
    trackStoryView(currentStory.id);
  }, [open, currentStory?.id, trackStoryView]);

  // ---- Reset on open ----
  useEffect(() => {
    if (open) {
      setCurrentStoryIndex(0);
      setProgress(0);
      setIsPlaying(true);
      setVideoDurationMs(null);
    }
  }, [open]);

  // Reset progress and video duration on story change
  useEffect(() => {
    setProgress(0);
    setVideoDurationMs(null);
  }, [currentStoryIndex]);

  // ---- Progress timer ----
  const storyDurationMs =
    currentStory?.mediaType === 'video'
      ? (videoDurationMs ?? DEFAULT_VIDEO_DURATION)
      : IMAGE_DURATION;

  useEffect(() => {
    if (!open || !isPlaying || !currentStory || stories.length === 0) return;

    const tickMs = 50;
    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + (100 / (storyDurationMs / tickMs));
        if (next >= 100) {
          if (currentStoryIndex < stories.length - 1) {
            setCurrentStoryIndex((i) => i + 1);
          } else {
            onClose();
          }
          return 0;
        }
        return next;
      });
    }, tickMs);

    return () => clearInterval(interval);
  }, [open, isPlaying, currentStoryIndex, stories.length, onClose, storyDurationMs, currentStory]);

  // ---- Video play/pause sync ----
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isPlaying) {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      v.pause();
    }
  }, [isPlaying, currentStoryIndex, stories.length]);

  // ---- Handlers ----
  const handleNext = useCallback(() => {
    if (currentStoryIndex < stories.length - 1) {
      setCurrentStoryIndex((i) => i + 1);
      setProgress(0);
    } else {
      onClose();
    }
  }, [currentStoryIndex, stories.length, onClose]);

  const handlePrevious = useCallback(() => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex((i) => i - 1);
      setProgress(0);
    }
  }, [currentStoryIndex]);

  const handleClose = useCallback(() => {
    setIsPlaying(false);
    onClose();
  }, [onClose]);

  const handleTogglePlay = useCallback(() => {
    setIsPlaying((p) => !p);
  }, []);

  const handleShopItems = () => {
    if (!currentStory?.items || currentStory.items.length === 0) return;
    const searchQuery = currentStory.items.join(', ');

    // Record the story click (unified record-click API, non-blocking)
    trackRetailerClick({
      platform: 'story',
      referenceId: currentStory.id
    }).catch((error) => {
      console.error('Story click tracking error:', error);
    });

    setIsPlaying(false);
    onClose();
    // storyId rides along with the master search so add-to-cart can attribute the sale
    navigate(buildOrderUrlWithSource('/retailer/order', searchQuery, 'story', currentStory.id));
  };

  // Keyboard
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!open) return;
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          handlePrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          handleNext();
          break;
        case 'Escape':
          event.preventDefault();
          handleClose();
          break;
        case ' ':
          event.preventDefault();
          handleTogglePlay();
          break;
      }
    },
    [open, handlePrevious, handleNext, handleClose, handleTogglePlay]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  // ---- Common paper styles (WhatsApp-like frame) ----
  const paperSx = {
    backgroundColor: '#0b141a',
    borderRadius: isMobile ? 0 : 3,
    width: isMobile ? '100vw' : 380,
    height: isMobile ? '100vh' : 720,
    maxWidth: isMobile ? '100vw' : 380,
    maxHeight: isMobile ? '100vh' : '95vh',
    position: 'relative' as const,
    overflow: 'hidden' as const,
    boxShadow: '0 30px 60px rgba(0,0,0,0.5)',
  };

  // ---- Loading state ----
  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth={false} fullScreen={isMobile} PaperProps={{ sx: paperSx }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <CircularProgress sx={{ color: 'white' }} />
          <Typography variant="body2" color="rgba(255,255,255,0.8)">{t('stories.loadingStories')}</Typography>
        </Box>
      </Dialog>
    );
  }

  // ---- Error state ----
  if (error) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth={false} fullScreen={isMobile} PaperProps={{ sx: paperSx }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, px: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="white">{t('stories.couldNotLoadStories')}</Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.7)">{error}</Typography>
          <Button onClick={handleClose} variant="outlined" sx={{ mt: 1, color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}>{t('common.close')}</Button>
        </Box>
      </Dialog>
    );
  }

  // ---- Empty state ----
  if (!currentStory || stories.length === 0) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth={false} fullScreen={isMobile} PaperProps={{ sx: paperSx }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, px: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="white">{t('stories.noStoriesYet')}</Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.7)">{t('stories.checkBackLater')}</Typography>
          <Button onClick={handleClose} variant="outlined" sx={{ mt: 1, color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}>{t('common.close')}</Button>
        </Box>
      </Dialog>
    );
  }

  const hasItems = !!(currentStory.items && currentStory.items.length > 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={false} fullScreen={isMobile} PaperProps={{ sx: paperSx }}>
      {/* Blurred backdrop using the same media (image only — adds depth like WhatsApp) */}
      {currentStory.mediaType === 'image' && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${currentStory.mediaUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(28px) brightness(0.45)',
            transform: 'scale(1.2)',
            zIndex: 0,
          }}
        />
      )}

      {/* Media (centered, contains, sits above blurred backdrop) */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1,
        }}
      >
        {currentStory.mediaType === 'image' ? (
          <img
            src={currentStory.mediaUrl}
            alt={currentStory.caption}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <video
            ref={videoRef}
            key={currentStory.id}
            src={currentStory.mediaUrl}
            autoPlay
            muted
            playsInline
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;
              if (Number.isFinite(d) && d > 0) {
                setVideoDurationMs(d * 1000);
              }
            }}
            onEnded={handleNext}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              backgroundColor: '#000',
            }}
          />
        )}
      </Box>

      {/* Pause indicator */}
      {!isPlaying && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'rgba(0,0,0,0.55)',
            borderRadius: '50%',
            width: 64,
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 6,
          }}
        >
          <PlayArrowIcon sx={{ color: 'white', fontSize: 36 }} />
        </Box>
      )}

      {/* Tap zones: left → previous, right → next, middle → toggle play/pause */}
      <Box
        onClick={handlePrevious}
        sx={{
          position: 'absolute',
          top: 56, // below header
          bottom: 0,
          left: 0,
          width: '30%',
          zIndex: 5,
          cursor: currentStoryIndex > 0 ? 'pointer' : 'default',
        }}
      />
      <Box
        onClick={handleTogglePlay}
        sx={{
          position: 'absolute',
          top: 56,
          bottom: 0,
          left: '30%',
          width: '40%',
          zIndex: 5,
          cursor: 'pointer',
        }}
      />
      <Box
        onClick={handleNext}
        sx={{
          position: 'absolute',
          top: 56,
          bottom: 0,
          right: 0,
          width: '30%',
          zIndex: 5,
          cursor: 'pointer',
        }}
      />

      {/* Progress bars */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          right: 8,
          zIndex: 10,
          display: 'flex',
          gap: 0.5,
        }}
      >
        {stories.map((_, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              height: 3,
              bgcolor: 'rgba(255,255,255,0.28)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <LinearProgress
              variant="determinate"
              value={index === currentStoryIndex ? progress : index < currentStoryIndex ? 100 : 0}
              sx={{
                height: 3,
                borderRadius: 2,
                backgroundColor: 'transparent',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'white',
                  borderRadius: 2,
                  transition: 'transform 50ms linear',
                },
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Header */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: 0,
          right: 0,
          zIndex: 10,
          px: 1.5,
          pt: 1.5,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: 'primary.main',
              fontSize: '0.875rem',
              border: '2px solid rgba(255,255,255,0.85)',
            }}
          >
            {logo ? (
              <img
                src={logo}
                alt={t('stories.logo')}
                style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }}
              />
            ) : (
              'W'
            )}
          </Avatar>
          <Box sx={{ lineHeight: 1.1 }}>
            <Typography variant="body2" color="white" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {t('stories.woopsa')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleTogglePlay} sx={{ color: 'white' }} size="small" aria-label={t('stories.playPause')}>
            {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </IconButton>
          <IconButton onClick={handleClose} sx={{ color: 'white' }} size="small" aria-label={t('common.close')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Caption + Shop Items CTA */}
      {(currentStory.caption || hasItems) && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            px: 2.5,
            pt: 5,
            pb: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.25,
            background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)',
          }}
        >
          {currentStory.caption && (
            <Typography
              variant="body1"
              color="white"
              sx={{
                textShadow: '0 2px 6px rgba(0,0,0,0.55)',
                textAlign: 'center',
                fontWeight: 500,
                lineHeight: 1.35,
              }}
            >
              {currentStory.caption}
            </Typography>
          )}
          {hasItems && (
            <Button
              variant="contained"
              size="medium"
              startIcon={<ShoppingCartIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleShopItems();
              }}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 999,
                px: 3,
                py: 0.75,
                boxShadow: '0 6px 16px rgba(0,0,0,0.35)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.92)' },
              }}
            >
              {currentStory.items && currentStory.items.length > 0
                ? t('stories.shopItemsCount', { count: currentStory.items.length })
                : t('stories.shopItems')}
            </Button>
          )}
        </Box>
      )}
    </Dialog>
  );
};

export default Stories;
